import areaUnitTransformer from "./areaUnitTransformer"
import bankTransformer from "./bankTransformer"
import { convertQueryObjToString, localisedValue } from "utils"
import leadStageTransformer from "./leadStageTransformer"
import leadsTransfomer from "./leadsTransformer"
import newTaskTransformer from "./newTaskTransformer"
import parentApi from "store/parentApi"
import paymentMethodTransformer from "./paymentMethodTransformer"
import paymentModeTransformer from "./paymentModeTransformer"
import agencyClientsTransformer from "./agencyClientsTransformer"
import platformTransformer from "./platformTransformer"
import sharedWithTransformer from "./sharedWithTransformer"
import sourcesTransformer from "./sourcesTransformer"
import taskStatusTransformer from "./taskStatusTransformer"
import { dateTimeFunction } from "utils/date"
import { DATE_TIME_FORMAT } from "constants"
import { accountantsTransformer } from "./accountantsTransformer"
import mortgageAssessmentTransformer from "./mortgageAssessmentTransformer"
import sharingTypeTransformer from "./sharingTypeTransformer"
import assessmentsTransformer from "./assessmentsTransformer"
import occupationTypesTransformer from "./occupationTypesTransformer"
import inquiriesWithAssessmentsTransformer from "./inquiriesWithAssessmentsTransformer"
import { bookingPlatformsTransformer } from "./bookingPlatformsTransformer"
import leadReviewTransformer from "./leadReviewsTransformer"
import sourcesFlatOptionsTransformer from "./sourcesFlatOptionsTransformer"
import reviewQestionsTransformer from "./reviewQuestionsTransformer"
import { buildPaymentEntry, normalizePaymentRows } from "./buildAddSalesPayload"
import fractionalProjectsTransformer from "./transformResponse/fractionalProjectsTransformer"
import fractionalDataTransformer from "./transformResponse/fractionalDataTransformer"
import fractionalPartnerFeesTransformer from "./transformResponse/fractionalPartnerFeesTransformer"

const clientApi = parentApi.injectEndpoints({
  endpoints: build => ({
    getIndustries: build.query({
      query: () => `/industries`,
      transformResponse: response => response.map(e => ({ ...e, label: e.name, value: e.id.toString() })),
    }),

    getPaymentMethod: build.query({
      query: ({ id } = {}) => {
        return id ? { url: `/projects/project/paymentModes?projectId=${id}` } : { url: `/paymentModes` }
      },
      transformResponse: (response, meta, { flag } = {}) => {
        const transformedData = paymentMethodTransformer(response, flag)
        return transformedData
      },
    }),
    getUnits: build.query({
      query: ({ projectId, module = null }) => {
        const queryString = convertQueryObjToString({ module })
        return `/definitions/getUnitsByProject/${projectId}${queryString ? `?${queryString}` : ""}`
      },
      transformResponse: response => {
        const transformedData = areaUnitTransformer(response)
        return transformedData
      },
    }),
    getNewTasks: build.query({
      query: () => `/taskTypes?taskTypeFor=lead&sortBy=displayOrder&sortDirection=asc&isActive=1`,
      transformResponse: response => {
        const transformedData = newTaskTransformer(response)
        return transformedData
      },
    }),
    getPaymentMode: build.query({
      query: parentId => `/paymentModes?parentId=${parentId}`,
      transformResponse: response => paymentModeTransformer(response),
    }),

    getBankName: build.query({
      query: () => `/banks`,
      transformResponse: response => {
        const transformedData = bankTransformer(response)
        return transformedData
      },
    }),

    getLeadDetails: build.query({
      query: ({ leadId, detailsType }) => {
        const queryObj = { detailsType: detailsType }
        return `/internalInquiries/${leadId}/leadDetails?${convertQueryObjToString(queryObj)}`
      },
      transformResponse: response => {
        const transformedData = leadsTransfomer(response)
        return transformedData
      },
    }),

    getValidateReceiptNumber: build.query({
      query: number => `/payments/validate/receipt?receiptNumber=${number}`,
    }),

    getValidateReferenceNumber: build.query({
      query: params => `payments/validate/referenceNumber?${convertQueryObjToString(params)}`,
    }),

    getLeadStages: build.query({
      query: () => `/InternalInquiryStages`,
      transformResponse: data => leadStageTransformer(data),
    }),

    getBuySellOverview: build.query({
      query: leadID => `/internalInquiries/${leadID}?includeSaleability=true`,
    }),

    getSellerRecommendedListings: build.query({
      query: params => `/listings/recommendedForSeller?${convertQueryObjToString(params)}`,
    }),

    getShortlistedListings: build.query({
      query: leadId => `/listingsShortlisted/${leadId}`,
      providesTags: ["shortlisted-leads"],
    }),

    getRecommendedListings: build.query({
      query: ({ leadId, params }) => `listings/recommendedForBuyer/${leadId}?${convertQueryObjToString(params)}`,
      providesTags: ["recommended-leads"],
    }),

    addShortlistedListings: build.mutation({
      query: obj => ({ url: "/listingsShortlisted", method: "POST", body: obj }),
      invalidatesTags: (result, _error, _args) => (result ? ["shortlisted-leads"] : []),
    }),

    deleteShortlistedListings: build.mutation({
      query: ({ overview, id }) => {
        let body = {
          listingId: id,
          inquiryId: overview?.InternalInquiryLocations?.[0]?.inquiryId,
        }
        return { url: `/listingsShortlisted`, method: "DELETE", body }
      },
      invalidatesTags: (result, _error, _args) => (result ? ["shortlisted-leads"] : []),
    }),

    getSourcesWithChildren: build.query({
      query: () => `/internalInquirySources/sourcesWithChildren`,
      transformResponse: (data, meta, args) =>
        args?.flatOptions ? sourcesFlatOptionsTransformer(data) : sourcesTransformer(data),
    }),

    getSharedWith: build.query({
      query: params =>
        `/teamMembers/privilegedUsers?sharedLeadUsers=true&module=leads&subModule=sharedLeadUsers&${convertQueryObjToString(
          params
        )}`,
      transformResponse: data => {
        return sharedWithTransformer(data)
      },
    }),

    getSharingType: build.query({
      query: () => `internalInquirySharings/types`,
      transformResponse: response => sharingTypeTransformer(response),
    }),

    getSharedBy: build.query({
      query: params =>
        `/teamMembers/privilegedUsers?${
          params ? convertQueryObjToString(params) : `sharedLeadByUsers=true&module=leads&subModule=sharedLeadByUsers`
        }`,
    }),

    getAllocatedTo: build.query({
      query: params =>
        `/teamMembers/privilegedUsers?module=leads&subModule=searchLeads${
          params?.all ? "" : "&allocatedLeadUsers=true"
        }`,
    }),

    getPlatform: build.query({
      query: () => `/leadSources`,
      transformResponse: data => {
        return platformTransformer(data)
      },
    }),

    // TODO: remove this and user from shared

    getLeadTaskStatus: build.query({
      query: params => `/internalInquiryStatuses${params ? `?${convertQueryObjToString(params)}` : ""}`,
      transformResponse: data => {
        return taskStatusTransformer(data)
      },
    }),

    addSales: build.mutation({
      query: ({
        lead,
        leadSalesInfo,
        bookingForm,
        banks,
        modeOfPayments,
        bookingPlatform,
        bookingPlatformAttachment,
        stateManager,
        optionalSelections,
        ...values
      }) => {
        const selectedOptionalKeys = Object.keys(optionalSelections)?.filter(key => optionalSelections?.[key])
        const paymentRows = normalizePaymentRows(values)
        const paymentEntries = paymentRows.map(row => buildPaymentEntry(row, modeOfPayments, banks))

        let features = stateManager?.featureTable?.features
          ?.filter(item => !item?.fixed)
          ?.map(item => ({
            id: item?.featureId,
            amountType: item?.amountType,
            amount: item?.amount,
          }))

        let provisionalUnitObj = {
          listingAgainst: "project",
          listingAgainstId: values?.project?.id,
          ...stateManager?.provisionalUnit,
        }

        const payload = {
          projectId: !!values?.project ? values.project.id : leadSalesInfo?.projectId,
          ...(bookingPlatform !== -1 && {
            bookingPlatform,
            ...(bookingPlatformAttachment && {
              bookingPlatformAttachment,
            }),
          }),
          ...(!values?.provisionalFormFilled && {
            listingId: !!values?.unit ? values.unit.id : leadSalesInfo?.listingId,
          }),
          ...(values?.numberOfBlocks && { numberOfBlocks: values?.numberOfBlocks }),
          //Project tags
          ...(values?.promoTagIds?.length && { promoTagIds: values?.promoTagIds }),
          dealPrice: stateManager?.dealPrice ?? leadSalesInfo?.dealPrice,
          ...(values?.cdpCommitment && { cdpCommitment: values.cdpCommitment }),
          Payments: paymentEntries,
          Client: {
            ...(values?.nationalId && { nationalId: values.nationalId }),
            ...(values?.dateOfBirth && { dateOfBirth: values.dateOfBirth }),
            ...(values?.country && { countryId: values.country.id }),
            ...(values?.city && { cityId: values.city.id }),
            ...(values?.nationality && { nationalityId: values.nationality.id }),
          },
          ...(values?.comment && { comment: values?.comment }),
          ...(!!bookingForm && { addBookingForm: bookingForm }),
          ...((!leadSalesInfo || !leadSalesInfo?.contracts[0]?.length) && features),
          ...(leadSalesInfo?.contracts?.[0] && {
            contractListingId: leadSalesInfo?.contracts?.[0]?.contractListingId,
          }),
          ...(values?.deadLine && {
            nextStatusDeadline: dateTimeFunction({
              date: values?.deadLine.format(DATE_TIME_FORMAT),
              timeZone: true,
              apiRequest: true,
            }),
          }),
          saleType: values?.saleType || leadSalesInfo?.contractType || "self_finance",
          ...((values?.saleType === "mortgage" || leadSalesInfo?.contractType === "mortgage") && {
            mortgageSubType: values?.bankLoanSubType || leadSalesInfo?.mortgageSubType || null,
          }),
          ...(values?.nextTask !== -1 && { nextStatusId: values?.nextTask }),
          ...(values?.provisionalFormFilled && { provisionalUnit: provisionalUnitObj }),
          source: "web_v2",
          ...(selectedOptionalKeys.length > 0 && { optionalVasFees: selectedOptionalKeys }),
        }

        return {
          url: `/internalInquiries/${lead?.id}/sale`,
          method: "POST",
          body: payload,
        }
      },
      invalidatesTags: (resp, error, args) =>
        !!error ? [] : ["client-detail", "todos", { type: "leads", id: args.lead.Client.id }, "leads"],
    }),

    addReviewLead: build.mutation({
      query: ({ phoneCallId, isEntityValid, reviewType, entityToReview, entity, inquiryId, ...values }) => {
        const reviewResponses = Object.entries(values).map(([key, value]) => ({
          questionKey: key,
          value: value === undefined ? null : value,
        }))
        const shouldSendEntityId = ["lead", "meeting", "call"].includes(entityToReview)
        const body = {
          ...(inquiryId && { inquiryId: inquiryId }),
          ...(phoneCallId && { phoneCallId }),
          feedbackType: reviewType,
          isEntityValid,
          entityType: entityToReview || null,
          ...(shouldSendEntityId && { [entityToReview]: entity?.id }),
          reviewResponses,
        }
        return { url: "/feedbacks/inquiry", method: "POST", body }
      },
      invalidatesTags: (_resp, error) => (!error ? ["leads"] : []),
    }),

    getAgencyClients: build.query({
      query: ({ transform, salesEventId, checkIn, ...params }) => {
        const queryParams = {
          ...params,
          ...(salesEventId ? { checkIn, salesEventId } : {}),
        }
        return `/clients/agencyClients?${convertQueryObjToString(queryParams)}`
      }, // Exclude `transform` from query URL
      transformResponse: (response, meta, arg) => {
        return !arg?.transform ? response : agencyClientsTransformer(response)
      },
    }),

    getRecentAgencyClients: build.query({
      query: params => `/clients/agencyClients?${convertQueryObjToString(params)}`,
    }),

    getAccountants: build.query({
      query: params => `/users/regionalAccountants`,
      transformResponse: response => accountantsTransformer(response),
    }),
    getDataFromImage: build.mutation({
      query: params => {
        let body = {
          attachmentTypeId: params.attachmentTypeId,
          file: params.file?.split(",")[1],
          name: params.name,
          projectId: params.projectId,
          detectPaymentMode: params?.detectPaymentMode,
          ...(params?.chequeDate && { chequeDate: params?.chequeDate }),
        }
        return { url: `/attachments/imageToText`, method: "POST", body }
      },
    }),
    getPaymentList: build.query({
      query: params => `/payments/searches?${convertQueryObjToString(params)}`,
    }),

    getPaymentUrl: build.query({
      query: contractId => `/payments/v2/${contractId}?receiptLink=true`,
    }),
    getLeadReviews: build.query({
      query: params => `/feedbacks/reviews?${convertQueryObjToString(params)}`,
      transformResponse: response => leadReviewTransformer(response),
    }),
    addCloseLost: build.mutation({
      query: ({ leadId, ...values }) => {
        const body = {
          comment: values.comment,
          closureReasonId: values.reason,
          source: "web_v2",
        }
        return { url: `/internalInquiries/${leadId}/markClosedLost`, method: "POST", body }
      },
      invalidatesTags: (result, _error, _args) => (result ? ["leads", "todos"] : []),
    }),
    getCloseLostReasons: build.query({
      query: () => `/internalInquiryClosureReasons`,
      transformResponse: response =>
        response.map(e => {
          return { ...e.response, name: e.reason, id: e?.id.toString() }
        }),
    }),

    getSaleEvent: build.query({
      query: id => `/calendarEvents/activeSaleEvents/${id}`,
    }),

    getContractExpiryDate: build.query({
      query: ({ listingId, params }) => `/listings/tokenExpiry/${listingId}?${convertQueryObjToString(params)}`,
    }),
    addMortgageAssessment: build.mutation({
      query: ({ values: payload, leadId }) => {
        const downPaymentExists = payload?.downPayment?.value != 0 && !isNaN(payload?.downPayment?.value)
        const { downPayment, dateOfBirth, occupationType, interestedCity, unitType, ...rest } = payload
        const body = {
          ...rest,
          ...(!payload?.propertyType && { propertyType: "primary" }),
          ...(dateOfBirth && { dateOfBirth: dateOfBirth }),
          ...(payload?.occupationType && { employmentTypeId: payload?.occupationType }),
          ...(payload?.interestedCity && { interestedCityId: +payload?.interestedCity }),
          ...(payload?.unitType && { unitTypeId: payload?.unitType }),
          tenor: payload?.tenor || 25,
          dealPrice: payload?.unitPrice,
          inquiryId: leadId,
          ...(downPaymentExists && {
            cdpCommitment: payload?.downPayment?.percentage,
            downPayment: payload?.unitPrice * (payload?.downPayment?.percentage / 100),
          }),
        }
        return { url: `/mortgageAssessments/calculateMortgage`, method: "POST", body }
      },
      transformResponse: response => mortgageAssessmentTransformer(response),
      invalidatesTags: (_resp, error) =>
        !error ? ["getInquiriesWithMortgageAssessments", "client-detail", "AssessmentsListFilter"] : [],
    }),
    shareLeadWithMortgageTeam: build.mutation({
      query: payload => {
        const body = {
          inquiryIds: payload?.lead?.map(item => item?.id),
          salary: payload?.salary,
          totalLiabilities: payload?.totalLiabilities,

          tenor: payload?.tenor || 20,
          cdpCommitment: payload?.downPayment?.percentage,
          dealPrice: payload?.unitPrice,
          downPayment: payload?.unitPrice * (payload?.downPayment?.percentage / 100),
          nationalityId: payload?.nationalityId,
          isFirstHome: payload?.isFirstHome,

          ...(payload?.listingId && { listingId: payload?.listingId }),
          ...(payload?.projectId && { projectId: payload?.projectId }),
          ...(payload?.interestedCity && { interestedCityId: payload?.interestedCity }),
          ...(payload?.unitType && { unitTypeId: payload?.unitType }),
          ...(payload?.occupationType && { employmentTypeId: payload?.occupationType }),
          ...(payload?.preferredUnitConstructionPhase && {
            preferredUnitConstructionPhase: payload?.preferredUnitConstructionPhase,
          }),
          propertyType: payload?.propertyType ? payload?.propertyType : "primary",
        }

        return {
          url: `/internalInquiries/shareWithMortgageManual/user/${payload?.userId}`,
          method: "PUT",
          body,
        }
      },
    }),

    unShareWithMortgage: build.mutation({
      query: params => {
        const payload = {
          inquiryIds: params?.leads?.map(item => item?.id),
        }
        return {
          url: `/internalInquiries/unShareWithMortgage`,
          method: "PUT",
          body: payload,
        }
      },
      invalidatesTags: (_resp, error) => (!error ? ["sharings", "leads"] : []),
    }),

    getLeadComments: build.query({
      query: leadId => `/internalInquiries/${leadId}/leadDetails`,
      transformResponse: response => {
        return response
      },
    }),

    getMortgageAssessment: build.query({
      query: params => `/mortgageAssessments/inquiry/${params?.leadId}`,
      transformResponse: response => mortgageAssessmentTransformer(response),
      providesTags: ["getMortgageAssessment"],
    }),
    editMortgageAssessment: build.mutation({
      query: ({ values: payload, assessmentId, inquiryId }) => {
        const downPaymentExists = payload?.downPayment?.value != 0 && !isNaN(payload?.downPayment?.value)
        const {
          downPayment,
          dateOfBirth,
          occupationType,
          interestedCity,
          unitType,
          preferredUnitConstructionPhase,
          ...rest
        } = payload
        const body = {
          ...rest,
          inquiryId: inquiryId,
          ...(dateOfBirth && { dateOfBirth: dateOfBirth }),
          propertyType: payload?.propertyType ? payload?.propertyType : "primary",
          ...(payload?.occupationType && { employmentTypeId: payload?.occupationType }),
          ...(payload?.interestedCity && { interestedCityId: +payload?.interestedCity }),
          ...(payload?.unitType && { unitTypeId: payload?.unitType }),
          ...(payload?.preferredUnitConstructionPhase && {
            preferredUnitConstructionPhase: payload?.preferredUnitConstructionPhase,
          }),
          tenor: payload?.tenor || 25,
          dealPrice: payload?.unitPrice,
          ...(downPaymentExists && {
            cdpCommitment: payload?.downPayment?.percentage,
            downPayment: payload?.unitPrice * (payload?.downPayment?.percentage / 100),
          }),
        }
        return { url: `/mortgageAssessments/${assessmentId}`, method: "PUT", body: body }
      },
      transformResponse: response => mortgageAssessmentTransformer(response),
      invalidatesTags: (_resp, error) => (!error ? ["AssessmentsListFilter"] : []),
    }),
    getInquiriesWithMortgageAssessments: build.query({
      query: clientId => `/mortgageAssessments/client/${clientId}`,
      transformResponse: response => inquiriesWithAssessmentsTransformer(response),
      providesTags: ["getInquiriesWithMortgageAssessments"],
    }),
    getMortgageAssessmentsForInquiry: build.query({
      query: params => `/mortgageAssessments/logs/inquiry/${params.leadId}`,
      transformResponse: response => assessmentsTransformer(response),
      providesTags: ["AssessmentsListFilter"],
    }),
    getMortgageAssessmentDetails: build.query({
      query: params => `/mortgageAssessments/${params.id}`,
      transformResponse: response => mortgageAssessmentTransformer(response),
    }),
    getOccupationTypes: build.query({
      query: () => `/occupations/employmentTypes`,
      transformResponse: response => occupationTypesTransformer(response),
    }),
    getProjectActiveBookingPlatforms: build.query({
      query: id => `/bookingPlatforms/platforms?projectId=${id}`,
      transformResponse: response => bookingPlatformsTransformer(response),
    }),

    removeFromAuditConcern: build.mutation({
      query: inquiryId => ({
        url: `/InternalInquiryAuditConcerns/${inquiryId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_resp, error) => (!error ? ["leads"] : []),
    }),
    getClientNotInterestedReasons: build.query({
      query: () => `/internalInquiries/clientNotInterestedReasons`,
      transformResponse: response =>
        response?.map(item => ({
          ...item,
          label: localisedValue({ object: item, key: "title" }),
          value: item?.id?.toString(),
        })),
    }),
    getClientConcerns: build.query({
      query: () => `/internalInquiries/areasOfImprovements`,
      transformResponse: response =>
        response?.map(item => ({
          ...item,
          label: localisedValue({ object: item, key: "title" }),
          value: item?.id?.toString(),
        })),
    }),

    getClientsRecommendedProjects: build.query({
      query: ({ clientId, params }) => {
        return `/salesPlaybook/${clientId}/recommended-projects?${convertQueryObjToString(params)}`
      },
      transformResponse: response => {
        const projects = Array.isArray(response?.data) ? response.data : []
        const transformedProjects = projects.map(project => {
          const transformed = { ...project }
          if (project.city || project.cityAR) {
            transformed.Address = {
              CategoryCity: {
                name: project.city || "",
                nameAR: project.cityAR || "",
              },
            }
            delete transformed.city
            delete transformed.cityAR
          }
          if (project.location || project.locationAR) {
            transformed.Location = {
              name: project.location || "",
              nameAR: project.locationAR || "",
            }
            delete transformed.location
            delete transformed.locationAR
          }

          return transformed
        })

        return transformedProjects.sort((a, b) => (b.interestedProject ? 1 : 0) - (a.interestedProject ? 1 : 0))
      },
    }),

    getClientsSalesPlaybookProjectDetails: build.query({
      query: projectId => `/salesPlaybook/${projectId}/sales-playbook-project-details`,
      transformResponse: response => response?.data,
    }),

    getSalesPlaybook: build.query({
      query: clientId => `/salesPlaybook/${clientId}/sales-playbook-details`,
    }),

    getSalesPlayBookClientDetails: build.query({
      query: clientId => `/salesPlaybook/client/${clientId}/details`,
      transformResponse: response => response?.data,
    }),

    getSalesPlayBookClientProfiling: build.query({
      query: clientId => `/salesPlaybook/${clientId}/profiling`,
      transformResponse: response => response?.data,
    }),

    getSalesPlayBookClientSummary: build.query({
      query: clientId => `/salesPlaybook/${clientId}/client-summary`,
      transformResponse: response => response?.data,
    }),
    getReviewFormQuestions: build.query({
      query: params => `/feedbacks/reviewFormConfig?${convertQueryObjToString(params)}`,
      transformResponse: (response, _meta, args) => reviewQestionsTransformer(response, args),
    }),
    getFractionalProjects: build.query({
      query: () => "/definitions/v1/fractional-projects",
      transformResponse: response => fractionalProjectsTransformer(response),
    }),
    getFractionalProjectData: build.query({
      query: projectId => `/definitions/v1/projects/${projectId}/fractional-data`,
      transformResponse: (response, meta, arg) => fractionalDataTransformer(response, meta, arg),
    }),
    fractionalPartnerFees: build.mutation({
      query: ({ inquiryId, numberOfBlocks, projectId, isPsid }) => ({
        url: `/internalInquiries/${inquiryId}/fractional-partner-fees`,
        method: "POST",
        body: { numberOfBlocks, projectId, isPsid },
      }),
      transformResponse: response => fractionalPartnerFeesTransformer(response),
    }),
    deleteShortlistedListings: build.mutation({
      query: ({ overview, id }) => {
        let body = {
          listingId: id,
          inquiryId: overview?.InternalInquiryLocations?.[0]?.inquiryId,
        }
        return { url: `/listingsShortlisted`, method: "DELETE", body }
      },
      invalidatesTags: (result, _error, _args) => (result ? ["shortlisted-leads"] : []),
    }),
    filerStatus: build.mutation({
      query: clientId => ({ url: `/clients/${clientId}/filer-status`, method: "PUT" }),
    }),
  }),

  overrideExisting: false,
})
export const {
  useLazyGetPaymentUrlQuery,
  useGetPaymentUrlQuery,
  useLazyGetPaymentListQuery,
  useLazyGetAccountantsQuery,
  useGetAccountantsQuery,
  useGetDataFromImageMutation,
  useGetIndustriesQuery,
  useLazyGetIndustriesQuery,
  useGetPaymentMethodQuery,
  useLazyGetPaymentMethodQuery,
  useLazyGetPaymentModeQuery,
  useLazyGetUnitsQuery,
  useGetUnitsQuery,
  useAddSalesMutation,
  useAddReviewLeadMutation,
  useGetNewTasksQuery,
  useLazyGetNewTasksQuery,
  useLazyGetLeadDetailsQuery,
  useLazyGetValidateReceiptNumberQuery,
  useLazyGetValidateReferenceNumberQuery,
  useLazyGetBankNameQuery,
  useGetLeadDetailsQuery,
  useLazyGetSourcesWithChildrenQuery,
  useGetSourcesWithChildrenQuery,
  useLazyGetSharedWithQuery,
  useLazyGetSharingTypeQuery,
  useLazyGetSharedByQuery,
  useLazyGetAllocatedToQuery,
  useLazyGetPlatformQuery,
  useLazyGetLeadTaskStatusQuery,
  useLazyGetTaskAddedByQuery,
  useLazyGetAgencyClientsQuery,
  useLazyGetRecentAgencyClientsQuery,
  useLazyGetLeadStagesQuery,
  useLazyGetBuySellOverviewQuery,
  useLazyGetShortlistedListingsQuery,
  useLazyGetRecommendedListingsQuery,
  useAddShortlistedListingsMutation,
  useDeleteShortlistedListingsMutation,
  useLazyGetSellerRecommendedListingsQuery,
  useLazyGetLeadReviewsQuery,
  useAddCloseLostMutation,
  useLazyGetCloseLostReasonsQuery,
  useGetSaleEventQuery,
  useLazyGetSaleEventQuery,
  useLazyGetContractExpiryDateQuery,
  useAddMortgageAssessmentMutation,
  useShareLeadWithMortgageTeamMutation,
  useUnShareWithMortgageMutation,
  useGetMortgageAssessmentQuery,
  useLazyGetMortgageAssessmentQuery,
  useEditMortgageAssessmentMutation,
  useGetInquiriesWithMortgageAssessmentsQuery,
  useLazyGetInquiriesWithMortgageAssessmentsQuery,
  useLazyGetMortgageAssessmentsForInquiryQuery,
  useLazyGetMortgageAssessmentDetailsQuery,
  useGetOccupationTypesQuery,
  useGetLeadCommentsQuery,
  useLazyGetLeadCommentsQuery,
  useGetProjectActiveBookingPlatformsQuery,
  useLazyGetProjectActiveBookingPlatformsQuery,
  useRemoveFromAuditConcernMutation,
  useLazyGetClientNotInterestedReasonsQuery,
  useLazyGetClientConcernsQuery,
  useGetClientsRecommendedProjectsQuery,
  useLazyGetClientsRecommendedProjectsQuery,
  useGetSalesPlaybookQuery,
  useLazyGetSalesPlaybookQuery,
  useGetClientsSalesPlaybookProjectDetailsQuery,
  useLazyGetClientsSalesPlaybookProjectDetailsQuery,
  useGetSalesPlayBookClientDetailsQuery,
  useLazyGetSalesPlayBookClientDetailsQuery,
  useGetSalesPlayBookClientProfilingQuery,
  useLazyGetSalesPlayBookClientProfilingQuery,
  useGetSalesPlayBookClientSummaryQuery,
  useLazyGetSalesPlayBookClientSummaryQuery,
  useLazyGetReviewFormQuestionsQuery,
  useGetFractionalProjectsQuery,
  useLazyGetFractionalProjectsQuery,
  useGetFractionalProjectDataQuery,
  useLazyGetFractionalProjectDataQuery,
  useFractionalPartnerFeesMutation,
  useFilerStatusMutation,
} = clientApi
